import {
  ApprovalDetail,
  CombinedFlatProduct,
  ProductDetailsLineItem,
  ProductsDetailsData,
} from 'ts/interfaces/productsDetails.interface'
import { parseDealCubeHistoryByLevel } from '../../utils/parseDealCubeHistory'

interface DealCubeProductDetail {
  lineNumber: number
  totalDiscountPercentage: number
  bdNet: number
  netRevenue: number
}

interface DealCubeHistoryDetail {
  level: string | number
  approversByLevel?: Array<{ decisionReasonCode: string }>
  productDetails: DealCubeProductDetail[]
}

interface ProductDetailsTotalsInput {
  totalBdNetPriceAmount?: number | null
  totalEstimatedQuantity?: number | null
}

const pickEditableValue = (
  authUpdated?: number | null,
  requestUpdated?: number | null,
  authValue?: number | null,
  requestValue?: number | null,
  fallback?: number | null
): number => {
  const selected = authUpdated ?? requestUpdated ?? authValue ?? requestValue ?? fallback
  return selected ?? 0
}

const getCompositeId = (detail: DealCubeHistoryDetail): string | null => {
  const code = detail.approversByLevel?.[0]?.decisionReasonCode
  if (!code) return null
  return `${String(detail.level).trim()}-${code}`
}

export const transformProductsDetailsData = (
  combinedFlatProducts: CombinedFlatProduct[],
  dealCubeHistoryDetails: DealCubeHistoryDetail[] = [],
  totals?: ProductDetailsTotalsInput
): ProductsDetailsData => {
  const historyByApproval = parseDealCubeHistoryByLevel(
    dealCubeHistoryDetails,
    getCompositeId,
    (detail) => detail.productDetails,
    (productDetail) => productDetail.lineNumber
  )

  const lineItems: ProductDetailsLineItem[] = combinedFlatProducts.map((item, index) => {
    const { product } = item
    const lineNumber = product.lineNumber

    const approvalData: Record<string, ApprovalDetail> = {}
    historyByApproval.forEach((lineMap, approvalId) => {
      const detail = lineMap.get(lineNumber)
      approvalData[approvalId] = {
        ogmPercent: detail?.netRevenue ?? null,
        totalDiscountPercentage: detail?.totalDiscountPercentage ?? null,
        bdNet: detail?.bdNet ?? null,
      }
    })

    return {
      id: lineNumber || index,
      lineTypeCode: product.lineTypeCode,
      productGenericNumberText: product.productGenericNumberText,
      productGenericDescriptionText: product.productGenericDescriptionText,
      assosiatePlCode: product.assosiatePlCode,
      estimatedQuantity: product.estimatedQuantity,
      dataPoints: undefined,
      totalDiscountRequestPercentageDb: product.totalDiscountRequestPercentage,
      totalDiscountRequestPercentageUpdated: product.totalDiscountRequestPercentageUpdated,
      totalDiscountAuthPercentage: product.totalDiscountAuthPercentage,
      totalDiscountAuthPercentageUpdated: product.totalDiscountAuthPercentageUpdated,
      requestBdNetPriceAmount: product.requestBdNetPriceAmount,
      requestBdNetPriceAmountUpdated: product.requestBdNetPriceAmountUpdated,
      authBdnetPriceAmount: product.authBdnetPriceAmount,
      authBdnetPriceAmountUpdated: product.authBdnetPriceAmountUpdated,
      requestNetMarginPercentageDb: product.requestNetMarginPercentage,
      requestNetMarginPercentageUpdated: product.requestNetMarginPercentageUpdated,
      authNetMarginPercentage: product.authNetMarginPercentage,
      authNetMarginPercentageUpdated: product.authNetMarginPercentageUpdated,
      totalDiscountRequestPercentage: pickEditableValue(
        product.totalDiscountAuthPercentageUpdated,
        product.totalDiscountRequestPercentageUpdated,
        product.totalDiscountAuthPercentage,
        product.totalDiscountRequestPercentage
      ),
      extendedRequestBdNet: pickEditableValue(
        product.authBdnetPriceAmountUpdated,
        product.requestBdNetPriceAmountUpdated,
        product.authBdnetPriceAmount,
        product.requestBdNetPriceAmount,
        product.extendedRequestBdNet
      ),
      requestNetMarginPercentage: pickEditableValue(
        product.authNetMarginPercentageUpdated,
        product.requestNetMarginPercentageUpdated,
        product.authNetMarginPercentage,
        product.requestNetMarginPercentage
      ),
      calcGuidanceFloorPercentage: product.calcGuidanceFloorPercentage ?? null,
      calcGuidanceTypicalPercentage: product.calcGuidanceTypicalPercentage ?? null,
      calcGuidanceExpertPercentage: product.calcGuidanceExpertPercentage ?? null,
      historicalBottom25DiscountPercentage: product.historicalBottom25DiscountPercentage ?? null,
      historicalMedianDiscountPercentage: product.historicalMedianDiscountPercentage ?? null,
      historicalTop25DiscountPercentage: product.historicalTop25DiscountPercentage ?? null,
      approvalData,
    }
  })

  return {
    lineItems,
    totals: {
      qty: totals?.totalEstimatedQuantity ?? 0,
      bdNet: totals?.totalBdNetPriceAmount ?? 0,
    },
  }
}
